import { authedFetch } from '@features/auth/api/authRefresh'
import { type VaultCipherBlob, type VaultItemEncryptedResponse, type VaultItemType } from '../types'

/**
 * Vault REST API client (T-D-Vault-A、backend `apps/api/src/vault/` と 1:1)。
 *
 * 設計判断:
 *   - 全 endpoint が `JwtAuthGuard` 保護のため、`authedFetch` (Bearer + 401 retry)
 *     を経由する。`apiPost` / `apiGet` (cookie 専用) は使わない。
 *   - request body は **暗号化済の wire format** (`type` + `cipher`)。caller が
 *     `vaultCipher.ts encryptVaultPayload` で先に暗号化してから渡す責務。
 *   - response は `VaultItemEncryptedResponse[]` / `VaultItemEncryptedResponse` /
 *     void (DELETE 204) のいずれか。caller は `vaultCipher.ts decryptVaultPayload`
 *     で復号して `VaultItem` に変換する。
 */

const VAULT_BASE = '/vault/items'
const TRASH_BASE = '/vault/trash'

/** POST / PUT の body shape (backend `CreateVaultItemDto` / `UpdateVaultItemDto` と一致)。 */
export interface VaultItemWireRequest {
  type: VaultItemType
  cipher: VaultCipherBlob
  /** フォルダ UUID (未分類は null / 省略)。T-D-Vault-Folder Phase 4。 */
  folderId?: string | null
}

/** GET /api/vault/items — 自分の全アイテム (新→旧、サーバ側 ordering)。 */
export const listVaultItems = (): Promise<VaultItemEncryptedResponse[]> =>
  authedFetch<VaultItemEncryptedResponse[]>(VAULT_BASE)

/** GET /api/vault/items/:id — 個別取得 (404 / 403 は `ApiError` で throw)。 */
export const getVaultItem = (id: string): Promise<VaultItemEncryptedResponse> =>
  authedFetch<VaultItemEncryptedResponse>(`${VAULT_BASE}/${encodeURIComponent(id)}`)

/** POST /api/vault/items — 新規作成 (201)。 */
export const createVaultItem = (
  request: VaultItemWireRequest,
): Promise<VaultItemEncryptedResponse> =>
  authedFetch<VaultItemEncryptedResponse>(VAULT_BASE, {
    method: 'POST',
    body: request,
  })

/** PUT /api/vault/items/:id — 全置換更新 (404 / 403 は `ApiError` で throw)。 */
export const updateVaultItem = (
  id: string,
  request: VaultItemWireRequest,
): Promise<VaultItemEncryptedResponse> =>
  authedFetch<VaultItemEncryptedResponse>(`${VAULT_BASE}/${encodeURIComponent(id)}`, {
    method: 'PUT',
    body: request,
  })

/**
 * DELETE /api/vault/items/:id — 204 No Content。
 *
 * `authedFetch` は `res.json()` を呼ぶため 204 を返さない。Vault DELETE 用に
 * 専用の minimal wrapper を持つ (204 を許容、body 不要)。
 */
export const deleteVaultItem = async (id: string): Promise<void> => {
  await authedFetch<void>(`${VAULT_BASE}/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  })
}

/** GET /api/vault/trash — ゴミ箱内アイテム (削除日の新→旧)。 */
export const listTrashItems = (): Promise<VaultItemEncryptedResponse[]> =>
  authedFetch<VaultItemEncryptedResponse[]>(TRASH_BASE)

/** POST /api/vault/trash/:id/restore — アイテムを通常状態へ復元。 */
export const restoreTrashItem = (id: string): Promise<VaultItemEncryptedResponse> =>
  authedFetch<VaultItemEncryptedResponse>(`${TRASH_BASE}/${encodeURIComponent(id)}/restore`, {
    method: 'POST',
  })

/** DELETE /api/vault/trash/:id — アイテムを物理削除 (204、復元不可)。 */
export const permanentlyDeleteTrashItem = async (id: string): Promise<void> => {
  await authedFetch<void>(`${TRASH_BASE}/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  })
}
